import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Star } from "lucide-react";
import { useAuthedApi, errMsg, formatINR, imgUrl } from "../../lib/api";
import AdminEditListing from "./AdminEditListing";

export default function AdminListings() {
  const api = useAuthedApi();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [onlyFeatured, setOnlyFeatured] = useState(false);

  const load = () => { api("get", "/admin/listings", null, { params: { q: q || undefined, seller_type: type || undefined, featured: onlyFeatured || undefined, page } }).then(setData); };
  useEffect(load, [q, type, page, onlyFeatured]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFeatured = async (c) => {
    try {
      await api("patch", `/admin/listings/${c.id}/featured`, { featured: !c.featured });
      toast.success(c.featured ? "Removed from featured" : "Pinned as featured");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.year} ${c.brand} ${c.model}? This cannot be undone.`)) return;
    try { await api("delete", `/listings/${c.id}`); toast.success("Listing removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-listings">
      <div className="flex flex-wrap gap-3">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(e.target.q.value); }}>
          <input name="q" className="lux-input sm:w-72" placeholder="Search brand, model, city, seller..." data-testid="admin-listings-search" />
          <button className="btn-gold !py-2 text-sm" data-testid="admin-listings-search-btn">Search</button>
        </form>
        <select className="lux-select !w-48" value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} data-testid="admin-listings-type">
          <option value="">All sellers</option><option value="private">Private owners</option><option value="dealer">Dealers</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer"><input type="checkbox" checked={onlyFeatured} onChange={(e) => { setPage(1); setOnlyFeatured(e.target.checked); }} data-testid="admin-listings-featured-only" />Featured only</label>
        {data && <p className="text-sm text-gray-400 self-center" data-testid="admin-listings-total">{data.total} listings</p>}
      </div>
      <div className="lux-card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5">
            <th className="p-4">Car</th><th>Seller</th><th>Location</th><th>Price</th><th>Status</th><th className="text-right p-4">Actions</th>
          </tr></thead>
          <tbody>
            {data?.items.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-listing-row-${c.id}`}>
                <td className="p-4"><Link to={`/cars/${c.id}`} className="flex items-center gap-3 hover:text-[#E5C158]"><img src={imgUrl(c.images[0])} alt="" className="w-16 h-10 object-cover rounded" /><span>{c.year} {c.brand} {c.model}{c.is_demo && <span className="ml-2 text-[9px] uppercase text-gray-500 border border-white/10 px-1.5 rounded">demo</span>}</span></Link></td>
                <td><p>{c.seller_name}</p><p className="text-xs text-gray-500">{c.seller_type === "dealer" ? "Dealer" : "Owner"} · {c.seller_phone}</p></td>
                <td className="text-gray-400">{c.city}, {c.state}</td>
                <td className="gold-text font-semibold">{formatINR(c.price)}</td>
                <td><span className={`text-xs ${c.hidden ? "text-red-400" : c.status === "sold" ? "text-amber-400" : "text-emerald-400"}`}>{c.hidden ? "hidden" : c.status}</span></td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button className={`btn-outline !px-3 !py-1.5 text-xs mr-2 ${c.featured ? "!bg-[#D4AF37] !text-black" : ""}`} onClick={() => toggleFeatured(c)} data-testid={`admin-feature-listing-${c.id}`}><Star className={`w-3 h-3 ${c.featured ? "fill-black" : ""}`} />{c.featured ? "Featured" : "Feature"}</button>
                  <button className="btn-outline !px-3 !py-1.5 text-xs mr-2" onClick={() => setEditing(c)} data-testid={`admin-edit-listing-${c.id}`}><Pencil className="w-3 h-3" />Edit</button>
                  <button className="btn-outline !px-3 !py-1.5 text-xs !text-red-400 !border-red-500/40" onClick={() => remove(c)} data-testid={`admin-delete-listing-${c.id}`}><Trash2 className="w-3 h-3" />Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.items.length && <p className="p-10 text-center text-gray-400">No listings found.</p>}
      </div>
      {data?.pages > 1 && (
        <div className="flex gap-2 justify-center">
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-full text-sm ${p === page ? "bg-[#D4AF37] text-black font-bold" : "border border-white/10"}`} data-testid={`admin-listings-page-${p}`}>{p}</button>
          ))}
        </div>
      )}
      {editing && <AdminEditListing car={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
